import { GEMINI_ASPECT_RATIOS } from "./gemini-config.js";
import { nearestAspectRatio } from "./video-config.js";
export const DEFAULT_PRINT_DPI = 300;
export const MAX_PRINT_PRESETS = 20;
/** iOS Safari canvas 면적 한도(약 16.7M px) 아래 — 키오스크가 canvas 로 최종 리사이즈하므로 초과 프리셋은 거부. */
export const MAX_TARGET_PIXELS = 16_000_000;
const EPOCH = "1970-01-01T00:00:00.000Z";
/**
 * 기본 프리셋 (HERIT 요청서 §4). "기존 사이즈"는 현재 3:4 세로 출력 — 4.5×6 in(114.3×152.4mm)로
 * 300dpi 에서 정확히 1350×1800 px. 엽서·명함은 요청서 규격 그대로.
 */
export const DEFAULT_PRINT_PRESETS = [
    { id: "preset_portrait_3x4", name: "기본 세로 (3:4)", widthMm: 114.3, heightMm: 152.4, dpi: DEFAULT_PRINT_DPI, createdAt: EPOCH, updatedAt: EPOCH },
    { id: "preset_postcard", name: "엽서", widthMm: 148, heightMm: 100, dpi: DEFAULT_PRINT_DPI, createdAt: EPOCH, updatedAt: EPOCH },
    { id: "preset_namecard", name: "명함(카드)", widthMm: 86, heightMm: 54, dpi: DEFAULT_PRINT_DPI, createdAt: EPOCH, updatedAt: EPOCH },
    // DNP DS620 4×6 롤 — Gemini 2:3/3:2 와 동일 비율(BOOTH-117)
    { id: "preset_4x6_portrait", name: "4×6 in 세로 (2:3)", widthMm: 101.6, heightMm: 152.4, dpi: DEFAULT_PRINT_DPI, createdAt: EPOCH, updatedAt: EPOCH },
    { id: "preset_4x6_landscape", name: "4×6 in 가로 (3:2)", widthMm: 152.4, heightMm: 101.6, dpi: DEFAULT_PRINT_DPI, createdAt: EPOCH, updatedAt: EPOCH },
];
export const DEFAULT_PRINT_PRESETS_CONFIG = {
    id: "default",
    presets: [...DEFAULT_PRINT_PRESETS],
    activePresetId: DEFAULT_PRINT_PRESETS[0].id,
    updatedAt: EPOCH,
};
export const PLAN_IMAGE_SIZES = ["1K", "2K", "4K"];
/**
 * Gemini 3.x image 모델의 imageSize × aspectRatio 별 출력 픽셀 참조표.
 * 공식 문서가 표를 노출하지 않아 공개된 값(Gemini 3 Pro Image)을 기준으로 작성 —
 * transform 워커가 실제 결과 크기를 로그로 남겨 불일치 시 이 표를 고친다.
 * 클라이언트가 어차피 targetPx 로 최종 리사이즈하므로 표의 오차는 화질(업스케일 여부)에만 영향.
 */
export const GEMINI_OUTPUT_PX = {
    "1K": {
        "1:1": { w: 1024, h: 1024 },
        "2:3": { w: 848, h: 1264 },
        "3:2": { w: 1264, h: 848 },
        "3:4": { w: 896, h: 1200 },
        "4:3": { w: 1200, h: 896 },
        "4:5": { w: 928, h: 1152 },
        "5:4": { w: 1152, h: 928 },
        "9:16": { w: 768, h: 1376 },
        "16:9": { w: 1376, h: 768 },
        "21:9": { w: 1584, h: 672 },
    },
    "2K": {
        "1:1": { w: 2048, h: 2048 },
        "2:3": { w: 1696, h: 2528 },
        "3:2": { w: 2528, h: 1696 },
        "3:4": { w: 1792, h: 2400 },
        "4:3": { w: 2400, h: 1792 },
        "4:5": { w: 1856, h: 2304 },
        "5:4": { w: 2304, h: 1856 },
        "9:16": { w: 1536, h: 2752 },
        "16:9": { w: 2752, h: 1536 },
        "21:9": { w: 3168, h: 1344 },
    },
    "4K": {
        "1:1": { w: 4096, h: 4096 },
        "2:3": { w: 3392, h: 5056 },
        "3:2": { w: 5056, h: 3392 },
        "3:4": { w: 3584, h: 4800 },
        "4:3": { w: 4800, h: 3584 },
        "4:5": { w: 3712, h: 4608 },
        "5:4": { w: 4608, h: 3712 },
        "9:16": { w: 3072, h: 5504 },
        "16:9": { w: 5504, h: 3072 },
        "21:9": { w: 6336, h: 2688 },
    },
};
/** imageSize 는 gemini-3.x image 계열만 지원. gemini-2.5-* 는 1024px 고정이라 파라미터를 보내지 않는다. */
export function modelSupportsImageSize(model) {
    return !model.toLowerCase().startsWith("gemini-2.5");
}
const MM_PER_INCH = 25.4;
/** mm × dpi → 픽셀 (반올림). */
export function presetTargetPx(p) {
    return {
        w: Math.round((p.widthMm / MM_PER_INCH) * p.dpi),
        h: Math.round((p.heightMm / MM_PER_INCH) * p.dpi),
    };
}
/** 목표 비율(w/h)에 로그 거리가 가장 가까운 Gemini 비율. 동률은 GEMINI_ASPECT_RATIOS 순서. (`nearestAspectRatio` 의 특수형) */
export function nearestGeminiAspectRatio(ratio) {
    return nearestAspectRatio(ratio, GEMINI_ASPECT_RATIOS);
}
/** 소스를 목표 비율로 중앙 크롭한 뒤에도 목표 픽셀을 덮는지 (업스케일 없이 리사이즈 가능한지). */
export function coversAfterCrop(source, target) {
    const tr = target.w / target.h;
    const cropW = Math.min(source.w, source.h * tr);
    const cropH = Math.min(source.h, source.w / tr);
    return cropW >= target.w && cropH >= target.h;
}
export function resolveOutputPlan(preset, model) {
    const targetPx = presetTargetPx(preset);
    const geminiAspectRatio = nearestGeminiAspectRatio(targetPx.w / targetPx.h);
    if (!modelSupportsImageSize(model)) {
        const sourcePx = GEMINI_OUTPUT_PX["1K"][geminiAspectRatio];
        return { preset, targetPx, geminiAspectRatio, imageSize: null, sourcePx, upscaled: !coversAfterCrop(sourcePx, targetPx) };
    }
    for (const size of PLAN_IMAGE_SIZES) {
        const sourcePx = GEMINI_OUTPUT_PX[size][geminiAspectRatio];
        if (coversAfterCrop(sourcePx, targetPx)) {
            return { preset, targetPx, geminiAspectRatio, imageSize: size, sourcePx, upscaled: false };
        }
    }
    const largest = PLAN_IMAGE_SIZES[PLAN_IMAGE_SIZES.length - 1];
    return {
        preset,
        targetPx,
        geminiAspectRatio,
        imageSize: largest,
        sourcePx: GEMINI_OUTPUT_PX[largest][geminiAspectRatio],
        upscaled: true,
    };
}
/** mm 비교 허용 오차 — 소수 1자리 입력이라 0.05mm 면 반올림 경계를 흡수한다. */
const MM_EPSILON = 0.05;
/**
 * 사진이 용지에 들어가는 방식. 용지가 없으면(= 용지 = 사진) 항상 "as-is".
 * "rotated" 는 그대로는 안 들어가지만 90° 돌리면 들어가는 경우,
 * "none" 은 어떻게 해도 안 들어가는 경우 — 콘솔이 경고로 알린다(저장은 막지 않는다).
 */
export function photoFitsPaper(photo, paper) {
    if (!paper)
        return "as-is";
    const fits = (w, h) => w <= paper.widthMm + MM_EPSILON && h <= paper.heightMm + MM_EPSILON;
    if (fits(photo.widthMm, photo.heightMm))
        return "as-is";
    if (fits(photo.heightMm, photo.widthMm))
        return "rotated";
    return "none";
}
/** 소수 2자리 mm 반올림 (0.1mm 입력의 절반이 0.05 라 2자리면 정확). */
function round2(n) {
    return Math.round(n * 100) / 100;
}
/**
 * 사진 규격 + 부스 용지 → 인쇄 배치. 키오스크 `printSheetCss` 와 콘솔 미리보기가 공유한다.
 * 용지가 없으면 용지 = 사진, 오프셋 0 (BOOTH-57 의 기존 출력과 동일).
 *
 * 용지를 받아야 하는 이유: 드라이버는 페이지 박스를 실제 용지에 맞춰 축소(fit-to-page)하므로
 * 페이지 = 실제 용지일 때만 사진이 지정한 mm 로 인쇄된다. 용지는 크기 고정 수단이고 여백은 그 부산물.
 * "none"(안 들어감)이면 as-is 로 두고 넘침은 그대로 — 프리셋은 전역이라 부스별 용지로 저장을 막을 수 없다.
 * 이때 오프셋은 음수가 되어(중앙 배치 그대로) 사진이 사방으로 균등하게 잘린다 — 0 으로 잘라 두면
 * 오른쪽·아래만 잘려 인물이 한쪽으로 치우친 인쇄물이 나온다.
 */
export function resolvePrintLayout(photoSize, paper) {
    const page = paper ?? { widthMm: photoSize.widthMm, heightMm: photoSize.heightMm };
    const rotate = photoFitsPaper(photoSize, paper) === "rotated";
    const photo = rotate
        ? { widthMm: photoSize.heightMm, heightMm: photoSize.widthMm }
        : { widthMm: photoSize.widthMm, heightMm: photoSize.heightMm };
    return {
        page,
        photo,
        rotate,
        offset: {
            xMm: round2((page.widthMm - photo.widthMm) / 2),
            yMm: round2((page.heightMm - photo.heightMm) / 2),
        },
    };
}
/** 활성 프리셋 — id 불일치면 첫 항목, 목록이 비었거나 문서가 없으면 기본 첫 항목. 워커가 절대 죽지 않게 항상 값을 돌려준다. */
export function resolveActivePreset(config) {
    const presets = config?.presets ?? [];
    return (presets.find((p) => p.id === config?.activePresetId) ??
        presets[0] ??
        DEFAULT_PRINT_PRESETS[0]);
}
//# sourceMappingURL=print-preset.js.map